import {FC, useState} from 'react'
import {useDatePicker} from '@rehookify/datepicker'
import {DateSelector} from '@components/commons/calendar-legacy/DateSelector.tsx'
import {YearSelector} from '@components/commons/calendar-legacy/YearSelector.tsx'
import {MonthSelector} from '@components/commons/calendar-legacy/MonthSelector.tsx'
import {Flexbox} from '@components/ui/flexbox/FlexBox.tsx'

export type View = 'date' | 'month' | 'year'

export type CalendarProps = {
    numMonths: number
    selectedDates: Date[]
    onDatesChange: (dates: Date[]) => void
    offsetDate?: Date
    excludedDates?: Date[]
    mode: 'single' | 'multiple' | 'range'
    toggle?: boolean
    minDate?: Date
    maxDate?: Date
    locale?: string
}
export const Calendar: FC<CalendarProps> = ({
    numMonths,
    selectedDates,
    onDatesChange,
    offsetDate,
    excludedDates,
    mode,
    toggle = false,
    minDate,
    maxDate,
    locale = 'en'
}) => {
    const {
        data: {weekDays, calendars, months, years},
        propGetters: {
            dayButton,
            addOffset,
            subtractOffset,
            monthButton,
            yearButton,
            nextYearsButton,
            previousYearsButton
        }
    } = useDatePicker({
        selectedDates,
        onDatesChange,
        offsetDate,
        exclude: excludedDates ? {date: excludedDates} : undefined,
        dates: {toggle, mode, minDate, maxDate},
        calendar: {
            startDay: 0,
            offsets: [...Array(numMonths).keys()].slice(1)
        }
    })
    const [view, setView] = useState<View>('date')

    return (
        <Flexbox>
            {view == 'year' ? (
                <YearSelector
                    setViewState={setView}
                    years={years}
                    yearButton={yearButton}
                    nextYearsButton={nextYearsButton}
                    previousYearsButton={previousYearsButton}
                    currentYear={calendars[0].year}
                />
            ) : view == 'month' ? (
                <MonthSelector
                    setViewState={setView}
                    months={months}
                    monthButton={monthButton}
                    currentMonth={calendars[0].month}
                />
            ) : (
                calendars.map((calendar, index) => (
                    <DateSelector
                        mode={mode}
                        setViewState={setView}
                        locale={locale}
                        key={`${calendar.month}-${calendar.year}`}
                        calendar={calendar}
                        dayButton={dayButton}
                        weekDays={weekDays}
                        subtractOffset={index == 0 ? subtractOffset : undefined}
                        addOffset={index == calendars.length - 1 ? addOffset : undefined}
                    />
                ))
            )}
        </Flexbox>
    )
}
